<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { Benutzer, BenutzerCollection } from '@/models/Benutzer';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'username' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 250);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new BenutzerCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: Benutzer) {
    router.push({ name: 'system_benutzer_edit', params: { id: r?.id } });
}

async function deleteRecord(r: Benutzer) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'system_benutzer_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('benutzer')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as Benutzer)"
            @deleterecord="deleteRecord($event as Benutzer)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="username" sortable :header="$t('benutzername')"></Column>
            <Column field="nachname" sortable :header="$t('nachname')"></Column>
            <Column field="vorname" sortable :header="$t('vorname')"></Column>
            <Column field="email" sortable :header="$t('email')">
                <template #body="colProps">
                    <a :href="`mailto:${colProps.data.email}`">{{ colProps.data.email }}</a>
                </template>
            </Column>
            <Column field="rolle" sortable :header="$t('rolle')"></Column>
            <Column field="letzter_login" sortable :header="$t('letzter_login')">
                <template #body="colProps">
                    <span>{{ colProps.data.letzter_login?.humanReadableDateTime() }}</span>
                </template>
            </Column>
            <Column field="anzahl_logins" sortable :header="$t('anzahl_logins')"></Column>
            <Column field="login_versuche" sortable :header="$t('login_versuche')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
